// Employee identity for the NEW admin SPA.
//
// Mirrors admin-internal's `employee-identity.ts` (the pattern is proven) but with
// its OWN localStorage keys so the two admin apps — served on different origins —
// never share a signed-in/out state. In cloud the SPA can't read the HttpOnly
// `admin_authority` cookie, so it learns its real email from GET /api/admin/me
// (fetched once at boot through the API client, which caches it here) and shows it
// in the shell. Locally the build-time stub email (VITE_DEV_EMPLOYEE_EMAIL) is the
// identity the `X-Employee-Email` header carries.

const EMPLOYEE_EMAIL_KEY = "recruiter_admin_app_employee_email";
// Set on log out, cleared on sign-in. While set, the SPA presents NO identity
// (effectiveEmployeeEmail returns null) so log out sticks in stub-mode backends,
// where the always-sent stub header would otherwise re-authenticate immediately.
const SIGNED_OUT_KEY = "recruiter_admin_app_signed_out";

function emailStorage(): Storage | undefined {
  try {
    return typeof localStorage !== "undefined" ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadCachedEmployeeEmail(): string | null {
  try {
    return emailStorage()?.getItem(EMPLOYEE_EMAIL_KEY) ?? null;
  } catch {
    return null;
  }
}

export function storeEmployeeEmail(email: string | null): void {
  try {
    const s = emailStorage();
    if (!s) return;
    if (email) s.setItem(EMPLOYEE_EMAIL_KEY, email);
    else s.removeItem(EMPLOYEE_EMAIL_KEY);
  } catch {
    // Private-mode / quota — identity won't persist but the app still works.
  }
}

/** Drop the cached identity on log out so the next load reads no stale email. */
export function clearCachedEmployeeEmail(): void {
  storeEmployeeEmail(null);
}

export function isSignedOut(): boolean {
  try {
    return emailStorage()?.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Mark signed out (on log out, before the reload). Persisted so the suppression
 *  survives the reload that renders the signed-out state. */
export function markSignedOut(): void {
  try {
    emailStorage()?.setItem(SIGNED_OUT_KEY, "1");
  } catch {
    // Private-mode / quota — the server cookie is still cleared in cloud; only the
    // stub-mode header suppression is best-effort here.
  }
}

/** Clear the flag when the operator signs back in (the sign-in / retry gestures,
 *  or a successful /api/admin/me in cloud once the Google cookie is set again). */
export function clearSignedOut(): void {
  try {
    emailStorage()?.removeItem(SIGNED_OUT_KEY);
  } catch {
    // no-op — see markSignedOut.
  }
}

/** The email the SPA should PRESENT right now: null while signed out (so no
 *  `X-Employee-Email` header is sent and stub-mode backends return not-signed-in),
 *  otherwise the cached real email or the caller's fallback. */
export function effectiveEmployeeEmail(fallback: string | null): string | null {
  if (isSignedOut()) return null;
  return loadCachedEmployeeEmail() ?? fallback;
}
